<template>
	<SideNavLayout>
		<div class="contact-research-page">
			<Input
				v-if="!hasSearchSession"
				:input="input"
				:providers="store.providers"
				:selected-provider-ids="selectedProviders"
				:loading-providers="loadingProviders"
				:provider-load-error="providerLoadError"
				:error="store.error"
				@run="runSearch"
			/>
			<template v-else>
				<div v-if="store.error" class="alert error" role="alert">{{ store.error }}</div>
				<Run
					v-if="!store.result"
					:input="input"
					:provider-progress="selectedProviderProgress"
					:running="store.isRunning"
					:failed="runFailed"
					:run-started-at="store.runStartedAt"
					:current-time="currentTime"
					@cancel="cancelSearch"
				/>
				<Result
					v-else
					:input="input"
					:result="store.result"
					:provider-progress="selectedProviderProgress"
					:providers="store.providers"
					:current-time="currentTime"
					:run-started-at="store.runStartedAt"
					@new-search="newSearch"
				/>
			</template>
		</div>
	</SideNavLayout>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, reactive, ref } from "vue"
import { storeToRefs } from "pinia"
import Input from "@/components/contact-research/Input.vue"
import Result from "@/components/contact-research/Result.vue"
import Run from "@/components/contact-research/Run.vue"
import SideNavLayout from "@/components/layout/SideNavLayout.vue"
import { useContactResearchStore } from "@/stores/contactResearchStore"
import { useToastStore } from "@/stores/toastStore"
import { defaultContactResearchProviderIds } from "@/utils/contactResearch"
import {
	ContactResearchProviderId,
	ContactResearchRunStatus,
	type ContactResearchInput,
	type ContactResearchProviderProgress,
} from "@/types/contactResearch"

const providerDisplayOrder = [
	ContactResearchProviderId.Hunter,
	ContactResearchProviderId.Canada411,
	ContactResearchProviderId.Surfe,
	ContactResearchProviderId.WebSearch,
]

function defaultInput(): ContactResearchInput {
	return {
		firstName: "",
		lastName: "",
		contactAddress: "",
		contactMunicipality: "",
		contactProvince: "QC",
		contactPostalCode: "",
		contactLinkedInUrl: "",
		companyName: "",
		companyAddress: "",
		companyMunicipality: "",
		companyProvince: "QC",
		companyPostalCode: "",
		companyLinkedInUrl: "",
		companyDomain: "",
		companyPhone: "",
		companyNeq: "",
	}
}

const store = useContactResearchStore()
const toastStore = useToastStore()
const { providerProgressById } = storeToRefs(store)
const input = reactive<ContactResearchInput>(defaultInput())
const selectedProviders = ref<ContactResearchProviderId[]>([])
const loadingProviders = ref(false)
const providerLoadError = ref("")
const hasSearchSession = ref(false)
const currentTime = ref(Date.now())
let elapsedTimer: ReturnType<typeof setInterval> | null = null

const selectedProviderProgress = computed(() =>
	selectedProviders.value
		.map(providerId => providerProgressById.value[providerId])
		.filter((progress): progress is ContactResearchProviderProgress => progress !== undefined)
		.sort(
			(firstProgress, secondProgress) =>
				providerDisplayOrder.indexOf(firstProgress.provider.id) -
				providerDisplayOrder.indexOf(secondProgress.provider.id)
		)
)

const runFailed = computed(() => store.status === ContactResearchRunStatus.Failed)

function newSearch(): void {
	store.clearRun()
	Object.assign(input, defaultInput())
	selectedProviders.value = defaultContactResearchProviderIds(store.providers)
	providerLoadError.value = ""
	currentTime.value = Date.now()
	hasSearchSession.value = false
}

function cancelSearch(): void {
	store.clearRun()
	hasSearchSession.value = false
}

async function runSearch(nextInput: ContactResearchInput, providerIds: ContactResearchProviderId[]): Promise<void> {
	Object.assign(input, nextInput)
	selectedProviders.value = providerIds
	hasSearchSession.value = true
	await store.startRun(nextInput, providerIds)

	if (store.result) {
		toastStore.success("contactResearchPage.result.savedAutomatically")
	}
}

onMounted(async () => {
	store.clearRun()

	elapsedTimer = setInterval(() => {
		currentTime.value = Date.now()
	}, 1000)

	loadingProviders.value = true

	try {
		const providers = await store.loadProviders()
		selectedProviders.value = defaultContactResearchProviderIds(providers)
	} catch (error) {
		providerLoadError.value = error instanceof Error ? error.message : String(error)
	} finally {
		loadingProviders.value = false
	}
})

onBeforeUnmount(() => {
	if (elapsedTimer) {
		clearInterval(elapsedTimer)
	}

	if (store.isRunning) {
		store.clearRun()
	}
})
</script>

<style scoped>
.contact-research-page {
	width: min(calc(100% - var(--spacing-2xl)), 1440px);
	margin: 0 auto;
	padding: var(--spacing-xl) 0 var(--spacing-2xl);
	color: var(--neutral-900);
}

:global(.layout-content:has(.contact-research-page)) {
	scrollbar-gutter: stable;
}

.alert {
	padding: var(--space-12) var(--space-14);
	margin-bottom: var(--space-16);
	font-size: var(--font-size-small);
	border: var(--border-width-thin) solid;
	border-radius: var(--border-radius-base);

	&.error {
		color: var(--accent-warning);
		background: color-mix(in srgb, var(--accent-warning) 7%, white);
		border-color: color-mix(in srgb, var(--accent-warning) 25%, white);
	}
}

@media (max-width: 640px) {
	.contact-research-page {
		width: min(calc(100% - var(--spacing-xl)), 1440px);
		padding-top: var(--spacing-md);
	}
}
</style>
